import isArray from 'lodash/isArray'
import { action, computed, flow, makeObservable, observable } from 'mobx'
import moment from 'moment'

import { requestWrapper } from 'Shared/utils'

import {
	entityToStringLabelValue,
	getDefaultStoreArrayValue,
	getDefaultStoreValue,
	getFilterValue,
	NameCodeToLabelValue,
	valueToStringLabelValue
} from 'Core/utils'

import { Kpi, KpiMetricFilterFields } from 'Portals/Customer/entities'
import { KPI_METRIC_START_YEAR } from 'Portals/Customer/Pages/MetricByPeriods/FiltersWrapper/Filters/utils/constants'
import { KpiRepo } from 'Portals/Customer/repositories'
import { RootStore } from 'Portals/Customer/stores'
import { RouteBuilder } from 'Portals/Customer/utils'

import { MetricByPeriodsTable } from './MetricByPeriodsTable'

export class MetricByPeriodsStore {
	table: MetricByPeriodsTable
	@observable accessor kpiMetricFilterOptions = getDefaultStoreArrayValue<Kpi.Metric.FilterOptions>()
	@observable accessor kpiById = getDefaultStoreValue<Kpi.Basic>()

	constructor(public rootStore: RootStore) {
		this.table = new MetricByPeriodsTable(rootStore)
	}

	@computed
	get kpiMetricOptions() {
		return this.kpiMetricFilterOptions.value.map((val) => NameCodeToLabelValue(val.category))
	}

	@computed
	get kpiOptions() {
		const selectedCategory = this.table.filters[KpiMetricFilterFields.kpiCategoryMetric]?.data
		const correctKpiCategory = getFilterValue(selectedCategory)
		return this.kpiMetricFilterOptions.value.reduce((acc, val) => {
			if (!correctKpiCategory) {
				val.kpis.forEach((kpi) => {
					acc.push(entityToStringLabelValue(kpi))
				})
				return acc
			}

			if (val.category.code === correctKpiCategory) {
				acc = val.kpis.map(entityToStringLabelValue)
			}
			return acc
		}, [] as LabelValue[])
	}

	@computed
	get kpiMetricYearOptions() {
		const currentYear = moment().year()

		const years = Array.from(
			{ length: currentYear - KPI_METRIC_START_YEAR + 1 },
			(_, index) => currentYear - index
		)

		return years.map(valueToStringLabelValue)
	}

	@computed
	get selectedKpi() {
		return this.kpiById.value
	}

	@flow.bound
	*retrieveMetricFilterOptions() {
		yield requestWrapper({
			field: this.kpiMetricFilterOptions,
			repository: KpiRepo.retrieveMetricFilterOptions,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveKpiById() {
		const id = this.table.filters[KpiMetricFilterFields.kpiId]?.data
		const correctId = isArray(id) ? id[0] : id
		if (!correctId) {
			return
		}
		yield requestWrapper({
			field: this.kpiById,
			repository: KpiRepo.getKpiById,
			repositoryParams: correctId as number
		})
	}

	@action.bound
	onChangeKpiCategoryFilter() {
		delete this.table.filters[KpiMetricFilterFields.kpiId]
		this.onChangeKpiFilter()
		this.kpiById = getDefaultStoreValue<Kpi.Basic>()
	}

	@action.bound
	onChangeKpiFilter() {
		this.retrieveKpiById()
		this.table.clearTableData()
		delete this.table.filters[KpiMetricFilterFields.year]
	}

	@action.bound
	clearMetricPeriodUrlParams() {
		const metricByPeriods = RouteBuilder.metricByPeriods.build()
		this.rootStore.history.replace(metricByPeriods)
	}

	@action.bound
	clearMetricPeriodFilters() {
		this.table.clearFilters()
		this.table.clearSorting()
		this.table.clearSearchFilters()
		this.clearMetricPeriodUrlParams()
		this.kpiById = getDefaultStoreValue<Kpi.Basic>()
	}
}
